import {
    ReactNode,
    ElementType,
    HTMLProps,
    MouseEvent,
    ForwardedRef,
    RefObject,
    forwardRef,
    useState,
    useLayoutEffect
} from 'react';

export type HoverProps<T extends ElementType = 'div', P = HTMLProps<HTMLDivElement>> = P & {
    component?: T;
    children: (hovering: boolean) => ReactNode;
};


const Hover = forwardRef(
    function Hover<T extends ElementType = 'div', P = HTMLProps<HTMLDivElement>>({
        component: Component = 'div' as any,
        children,
        ...props
    }: HoverProps<T, P>, ref: ForwardedRef<P>) {
        const [hovering, setHovering] = useState(false);

        const makeMouseHandler = (type: 'onMouseEnter' | 'onMouseLeave') => (event: MouseEvent) => {
            const propHandler = (props as any)[type] as ((event: MouseEvent) => void) | undefined;
            propHandler && propHandler(event);
            if (event.defaultPrevented) return;
            setHovering(type === 'onMouseEnter');
        };

        return (
            <div
                {...props}
                ref={ref as any}
                onMouseEnter={makeMouseHandler('onMouseEnter')}
                onMouseLeave={makeMouseHandler('onMouseLeave')}
            >
                {children(hovering)}
            </div>
        );
    }
);

export default Hover;

export const useHover = (ref: RefObject<HTMLElement>) => {
    const [hovering, setHovering] = useState(false);

    useLayoutEffect(() => {
        const { current } = ref;
        if (!current) return;

        const onEnter = () => setHovering(true);
        const onLeave = () => setHovering(false);
        current.addEventListener('mouseenter', onEnter);
        current.addEventListener('mouseleave', onLeave);

        return () => {
            current.removeEventListener('mouseenter', onEnter);
            current.removeEventListener('mouseleave', onLeave);
        };
    }, [ref]);
    return hovering;
};
